/**
 * Shopify Cart API Service
 * 
 * Service for interacting with Shopify's Storefront API for cart operations.
 * Handles cart creation, updates, and synchronization with the local cart state.
 * 
 * Features:
 * - Create and manage Shopify carts
 * - Add/remove/update cart lines
 * - Sync local cart with Shopify cart
 * - Handle cart checkout URLs
 * - Error handling and retry logic
 */

import shopifyApi from './shopifyApi';
import { CartItem } from '../contexts/ShoppingCartContext';
import { debugError, debugLog, debugWarn } from '../utils/debugLog';

// Shopify Cart interfaces
export interface ShopifyCart {
  id: string;
  checkoutUrl: string;
  totalQuantity: number;
  attributes?: Array<{
    key: string;
    value: string;
  }>;
  cost: {
    totalAmount: {
      amount: string;
      currencyCode: string;
    };
    subtotalAmount: {
      amount: string;
      currencyCode: string;
    };
    totalTaxAmount: {
      amount: string;
      currencyCode: string;
    } | null;
  };
  lines: {
    edges: Array<{
      node: ShopifyCartLine;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ShopifyCartLine {
  id: string;
  quantity: number;
  merchandise: {
    id: string; // Variant ID
    title: string;
    product: {
      id: string;
      title: string;
      handle: string;
    };
    price: {
      amount: string;
      currencyCode: string;
    };
    selectedOptions: Array<{
      name: string;
      value: string;
    }>;
  };
  cost: {
    totalAmount: {
      amount: string;
      currencyCode: string;
    };
  };
}

export interface CartLineInput {
  merchandiseId: string; // Variant ID
  quantity: number;
  attributes?: Array<{
    key: string;
    value: string;
  }>;
}

export interface CartInput {
  lines: CartLineInput[];
  attributes?: Array<{
    key: string;
    value: string;
  }>;
  buyerIdentity?: {
    countryCode?: string;
    email?: string;
    phone?: string;
  };
}

// Local storage key for Shopify cart ID
const SHOPIFY_CART_ID_KEY = 'eruption-radio-shopify-cart-id';

const HEADLESS_CART_ATTRIBUTES = {
  SOURCE: 'eruption_source',
  RETURN_URL: 'eruption_return_url',
} as const;

const getCheckoutReturnUrl = (): string => {
  const overrideBase = process.env.REACT_APP_CHECKOUT_RETURN_URL_BASE?.trim();
  const base = (() => {
    if (!overrideBase) {
      return typeof window !== 'undefined' ? window.location.origin : '';
    }

    try {
      const url = new URL(overrideBase);
      return `${url.protocol}//${url.host}`;
    } catch {
      return typeof window !== 'undefined' ? window.location.origin : '';
    }
  })();

  return `${base}/store?checkout=complete`;
};

// Cart API service class
export class ShopifyCartService {
  private cartId: string | null = null;

  constructor() {
    // Load existing cart ID from localStorage
    this.cartId = localStorage.getItem(SHOPIFY_CART_ID_KEY);
  }

  /**
   * Create a new Shopify cart with UK buyer identity
   */
  async createCart(items: CartItem[]): Promise<ShopifyCart> {
    const lines: CartLineInput[] = items.map(item => ({
      merchandiseId: item.variantId,
      quantity: item.quantity,
      attributes: Object.entries(item.selectedOptions || {}).map(([key, value]) => ({
        key,
        value,
      })),
    }));

    const returnUrl = getCheckoutReturnUrl();

    const input: CartInput = { 
      lines,
      attributes: [
        { key: HEADLESS_CART_ATTRIBUTES.SOURCE, value: 'headless' },
        { key: HEADLESS_CART_ATTRIBUTES.RETURN_URL, value: returnUrl },
      ],
      buyerIdentity: {
        countryCode: 'GB' // Set UK as default country for currency consistency
      }
    };

    const response = await shopifyApi.createCart(input);

    if (response.cartCreate.userErrors.length > 0) {
      throw new Error(`Cart creation failed: ${response.cartCreate.userErrors[0].message}`);
    }

    const cart = response.cartCreate.cart;
    this.cartId = cart.id;
    
    // Save cart ID to localStorage
    localStorage.setItem(SHOPIFY_CART_ID_KEY, cart.id);

    // Verify currency is GBP
    if (cart.cost.totalAmount.currencyCode !== 'GBP') {
      debugLog(`Cart created with currency ${cart.cost.totalAmount.currencyCode}, expected GBP`);
    }

    return cart;
  }

  /**
   * Update cart buyer identity to ensure UK currency
   */
  async updateBuyerIdentity(countryCode: string = 'GB'): Promise<ShopifyCart> {
    if (!this.cartId) {
      throw new Error('No cart ID available');
    }

    const response = await shopifyApi.updateCartBuyerIdentity(this.cartId, {
      countryCode
    });

    if (response.cartBuyerIdentityUpdate.userErrors.length > 0) {
      throw new Error(`Buyer identity update failed: ${response.cartBuyerIdentityUpdate.userErrors[0].message}`);
    }

    const cart = response.cartBuyerIdentityUpdate.cart;
    
    // Verify currency is GBP
    if (cart.cost.totalAmount.currencyCode !== 'GBP') {
      debugLog(`Cart currency is ${cart.cost.totalAmount.currencyCode}, expected GBP`);
    }

    return cart;
  }

  /**
   * Get existing Shopify cart
   */
  async getCart(): Promise<ShopifyCart | null> {
    if (!this.cartId) {
      return null;
    }

    try {
      const response = await shopifyApi.getCart(this.cartId);

      return response.cart;
    } catch (error) {
      debugError('Failed to fetch cart:', error);
      // Clear invalid cart ID
      this.clearCartId();
      return null;
    }
  }

  /**
   * Add lines to existing cart
   */
  async addCartLines(lines: CartLineInput[]): Promise<ShopifyCart> {
    if (!this.cartId) {
      throw new Error('No cart ID available');
    }

    const response = await shopifyApi.addToCart(this.cartId, lines);

    if (response.cartLinesAdd.userErrors.length > 0) {
      throw new Error(`Add cart lines failed: ${response.cartLinesAdd.userErrors[0].message}`);
    }

    return response.cartLinesAdd.cart;
  }

  /**
   * Update cart line quantities
   */
  async updateCartLines(lines: Array<{ id: string; quantity: number }>): Promise<ShopifyCart> {
    if (!this.cartId) {
      throw new Error('No cart ID available');
    }

    const response = await shopifyApi.updateCartLines(this.cartId, lines);

    if (response.cartLinesUpdate.userErrors.length > 0) {
      throw new Error(`Update cart lines failed: ${response.cartLinesUpdate.userErrors[0].message}`);
    }

    return response.cartLinesUpdate.cart;
  }

  /**
   * Remove lines from cart
   */
  async removeCartLines(lineIds: string[]): Promise<ShopifyCart> {
    if (!this.cartId) {
      throw new Error('No cart ID available');
    }

    const response = await shopifyApi.removeFromCart(this.cartId, lineIds);

    if (response.cartLinesRemove.userErrors.length > 0) {
      throw new Error(`Remove cart lines failed: ${response.cartLinesRemove.userErrors[0].message}`);
    }

    return response.cartLinesRemove.cart;
  }

  /**
   * Sync local cart items with Shopify cart
   */
  async syncCart(localItems: CartItem[]): Promise<ShopifyCart> {
    try {
      // Try to get existing cart
      let shopifyCart = await this.getCart();

      if (!shopifyCart) {
        // Create new cart if none exists
        shopifyCart = await this.createCart(localItems);
        return shopifyCart;
      }

      // Ensure headless cart attributes are present (for thank-you page extensions / return buttons)
      shopifyCart = await this.ensureHeadlessAttributes(shopifyCart);

      // Compare local items with Shopify cart lines
      const shopifyLines = shopifyCart.lines.edges.map(edge => edge.node);
      const linesToAdd: CartLineInput[] = [];
      const linesToUpdate: Array<{ id: string; quantity: number }> = [];
      const linesToRemove: string[] = [];

      // Create maps for easier comparison
      const localItemsMap = new Map<string, CartItem>();
      localItems.forEach(item => {
        const key = `${item.variantId}_${JSON.stringify(item.selectedOptions || {})}`;
        localItemsMap.set(key, item);
      });

      const shopifyLinesMap = new Map<string, ShopifyCartLine>();
      shopifyLines.forEach(line => {
        // Handle case where selectedOptions might be undefined or null
        const selectedOptions = (line.merchandise.selectedOptions || []).reduce((acc, option) => {
          acc[option.name] = option.value;
          return acc;
        }, {} as Record<string, string>);
        const key = `${line.merchandise.id}_${JSON.stringify(selectedOptions)}`;
        shopifyLinesMap.set(key, line);
      });

      // Find items to add or update
      localItemsMap.forEach((localItem, key) => {
        const shopifyLine = shopifyLinesMap.get(key);
        
        if (!shopifyLine) {
          // Item doesn't exist in Shopify cart, add it
          linesToAdd.push({
            merchandiseId: localItem.variantId,
            quantity: localItem.quantity,
            attributes: Object.entries(localItem.selectedOptions || {}).map(([k, v]) => ({
              key: k,
              value: v,
            })),
          });
        } else if (shopifyLine.quantity !== localItem.quantity) {
          // Item exists but quantity is different, update it
          linesToUpdate.push({
            id: shopifyLine.id,
            quantity: localItem.quantity,
          });
        }
      });

      // Find items to remove
      shopifyLinesMap.forEach((shopifyLine, key) => {
        if (!localItemsMap.has(key)) {
          linesToRemove.push(shopifyLine.id);
        }
      });

      // Execute updates in sequence
      if (linesToRemove.length > 0) {
        shopifyCart = await this.removeCartLines(linesToRemove);
      }

      if (linesToUpdate.length > 0) {
        shopifyCart = await this.updateCartLines(linesToUpdate);
      }

      if (linesToAdd.length > 0) {
        shopifyCart = await this.addCartLines(linesToAdd);
      }

      // Ensure UK currency by checking buyer identity
      if (shopifyCart.cost.totalAmount.currencyCode !== 'GBP') {
        debugLog(`Cart currency is ${shopifyCart.cost.totalAmount.currencyCode}, updating to GBP`);
        try {
          shopifyCart = await this.updateBuyerIdentity('GB');
        } catch (error) {
          debugError('Failed to update buyer identity for UK currency:', error);
        }
      }

      return shopifyCart;
    } catch (error) {
      debugError('Cart sync failed:', error);
      throw error;
    }
  }

  private async ensureHeadlessAttributes(cart: ShopifyCart): Promise<ShopifyCart> {
    if (!this.cartId) {
      return cart;
    }

    const desiredReturnUrl = getCheckoutReturnUrl();
    const existingAttributes = Array.isArray(cart.attributes) ? cart.attributes : [];
    const attributeMap = new Map(existingAttributes.map(attr => [attr.key, attr.value]));

    const needsSource = attributeMap.get(HEADLESS_CART_ATTRIBUTES.SOURCE) !== 'headless';
    const needsReturnUrl = attributeMap.get(HEADLESS_CART_ATTRIBUTES.RETURN_URL) !== desiredReturnUrl;

    if (!needsSource && !needsReturnUrl) {
      return cart;
    }

    attributeMap.set(HEADLESS_CART_ATTRIBUTES.SOURCE, 'headless');
    attributeMap.set(HEADLESS_CART_ATTRIBUTES.RETURN_URL, desiredReturnUrl);

    const mergedAttributes = Array.from(attributeMap.entries()).map(([key, value]) => ({
      key,
      value: value ?? '',
    }));

    try {
      const response = await shopifyApi.updateCartAttributes(this.cartId, mergedAttributes);
      const userErrors = response?.cartAttributesUpdate?.userErrors ?? [];
      if (userErrors.length > 0) {
        debugWarn('Headless cart attribute backfill failed (continuing checkout):', userErrors[0]);
        return cart;
      }

      const updatedCart = response?.cartAttributesUpdate?.cart;
      if (!updatedCart) {
        debugWarn('Headless cart attribute backfill returned no cart (continuing checkout)');
        return cart;
      }

      return {
        ...cart,
        attributes: updatedCart.attributes,
        checkoutUrl: updatedCart.checkoutUrl || cart.checkoutUrl,
      };
    } catch (error) {
      debugWarn('Headless cart attribute backfill failed (continuing checkout):', error);
      return cart;
    }
  }

  /**
   * Get checkout URL for current cart
   */
  async getCheckoutUrl(): Promise<string | null> {
    try {
      const cart = await this.getCart();
      return cart?.checkoutUrl || null;
    } catch (error) {
      debugError('Failed to get checkout URL:', error);
      return null;
    }
  }

  /**
   * Clear cart ID (when cart becomes invalid)
   */
  private clearCartId(): void {
    this.cartId = null;
    localStorage.removeItem(SHOPIFY_CART_ID_KEY);
  }

  /**
   * Clear the current cart (public method)
   */
  async clearCart(): Promise<void> {
    this.clearCartId();
  }

  /**
   * Get current cart ID
   */
  getCartId(): string | null {
    return this.cartId;
  }
}

// Export singleton instance
export const shopifyCartService = new ShopifyCartService();
